import { TransportOperatorService } from 'src/app/angularJS/services/transport-operator/transport-operator.service';
import { ACTToolbarService } from 'src/app/angularJS/services/toolbar.service';
import { PdfGeneratorService } from 'src/app/angularJS/services/pdf-generator.service';
import { FileBuilderService } from 'src/app/angularJS/services/file-builder/file-builder.service';
import { FileExporterService } from 'src/app/angularJS/services/file-exporting/file-exporter.service';
import { UserService } from 'src/app/angularJS/services/user/user.service';
import { QueryBuilder } from 'src/app/angularJS/services/query-builder.service';
import { JourneyUpdateService } from 'src/app/angularJS/services/journey-update/journey-update.service';
import { BreadcrumbService } from 'src/app/angularJS/services/breadcrumbs/breadcrumb.service';

/**
 * A Controller using the {@link actora.services.transportOperatorService transportOperatorService}
 * to handle the retrieval and display of transport operators.
 */
export class OperatorsListController {
  public static $inject = [
    'transportOperatorService',
    'ACTToolbarService',
    '$rootScope',
    '$translate',
    'PrimaryAction',
    '$state',
    '$stateParams',
    'pdfGeneratorService',
    'fileBuilderService',
    'fileExporterService',
    'userService',
    'QueryBuilder',
    'statusConstants',
    'journeyUpdateService',
    'breadcrumbService'
  ];

  public currentLocale: string;
  public paginationData: InterACT.Interfaces.IPaginationData;
  public operators: Array<InterACT.Interfaces.TransportOperators.TransportOperator>;
  public exportFormats: Array<string>;
  public currentFilter: string;

  private selectedScheme: InterACT.Interfaces.Schemes.Scheme;

  constructor(
    private transportOperatorService: TransportOperatorService,
    private toolbarService: ACTToolbarService,
    private $rootScope,
    private $translate: ng.translate.ITranslateService,
    public PrimaryAction: any,
    private $state: ng.ui.IStateService,
    private $stateParams,
    private pdfGeneratorService: PdfGeneratorService,
    private fileBuilderService: FileBuilderService,
    private fileExporterService: FileExporterService,
    private userService: UserService,
    private queryBuilder: QueryBuilder,
    private statusConstants: InterACT.Interfaces.IStatusConstants,
    private journeyUpdateService: JourneyUpdateService,
    private breadcrumbService: BreadcrumbService
  ) {
    this.activate();
  }

  /**
   * Function to retrieve records from the transport operators Service
   * @param page The number of the page of data to be retrieved
   * @param perPage The number of items per page to be retrieved
   * @param searchText Text to be used for querying the records
   * @returns The response object returned from a httpPromise
   */
  public fetchCollection = (page?: number, perPage?: number) => {
    this.setPageData(page, perPage);
    this.$rootScope.showSpinner = true;

    return this.transportOperatorService.getList(
      this.paginationData.currentPage,
      this.paginationData.perPageCount,
      this.constructQParam()
    ).then((result) => {
      this.operators = result.transportOperators;
      this.paginationData.totalCount = result.meta.totalCount;
      this.$rootScope.showSpinner = false;
    });
  }

  /**
   * Function to change the current state to 'operators-create'
   */
  public goToCreate = () => {
    this.$state.go('operators-create');
  }

  /**
   * Requests an exported version of the operators
   * @param format the export format
   */
  public exportTo = (format: string) => {
    const title = this.$translate.instant('OPERATORS');
    switch (format) {
      case 'CSV':
      case 'XLSX':
        this.$rootScope.showSpinner = true;
        this.fileBuilderService.buildExcelFile(
          '/transport-operators',
          this.constructQParam(),
          this.getOperatorColumns(),
          this.currentLocale,
          format.toLowerCase(),
          title).then((response) => {
            this.$rootScope.showSpinner = false;
            this.fileExporterService.exportToExcel(response.data, format.toLowerCase(), title);
          });
        break;
      default:
        this.exportToPdf(title);
    }
  }

  /**
   * Returns a list of the operator columns
   * @returns the list of operator columns
   */
  public getOperatorColumns = () => {
    return [
      { header: this.$translate.instant('NAME'), key: 'name' },
      { header: this.$translate.instant('OPERATOR_DETAIL.LICENCE_NUMBER'), key: 'licenceNumber' },
      { header: this.$translate.instant('OPERATOR_DETAIL.OID'), key: 'oid' },
      { header: this.$translate.instant('OPERATOR_DETAIL.SPID_NAME'), key: 'spid' },
      { header: this.$translate.instant('OPERATOR_DETAIL.TRADING_NAME'), key: 'tradingName' },
      { header: this.$translate.instant('ADDRESS'), key: 'address' }
    ];
  }

  /**
   * Exports the operator list to pdf
   * @param {string} title the title of the pdf
   */
  public exportToPdf = (title: string) => {
    this.transportOperatorService.getList(undefined, undefined, this.constructQParam()).then((result) => {
      const columnWidths = ['*', 100, 50, 50, '*', '*'];
      const headerRow = [
        this.$translate.instant('NAME'),
        this.$translate.instant('OPERATOR_DETAIL.LICENCE_NUMBER'),
        this.$translate.instant('OPERATOR_DETAIL.OID'),
        this.$translate.instant('OPERATOR_DETAIL.SPID_NAME'),
        this.$translate.instant('OPERATOR_DETAIL.TRADING_NAME'),
        this.$translate.instant('ADDRESS')
      ];
      const sourceData = _.map(result.transportOperators, (operator: any) => {
        return [
          operator.name[this.currentLocale],
          operator.licenceNumber,
          (<any>operator.fields).OID ? (<any>operator.fields).OID : '',
          (<any>operator.fields).SPID ? (<any>operator.fields).SPID : '',
          operator.tradingName.join(', '),
          _.values(operator.address).join(', ')
        ];
      });

      this.pdfGeneratorService.exportAsTable(headerRow, sourceData, columnWidths, title);
    });
  }

  /**
   * Wraps a call to the fetchCollection method passing in any filter text supplied
   * but defaulting the paging params to undefined values
   * @param filterText Text to be used when searching records
   */
  public onOperatorFilterChanged = (filterText?: string) => {
    this.currentFilter = filterText;
    this.paginationData.currentPage = 1;
    this.fetchCollection();
  }

  /**
   * Method used to rematch operator journeys
   *
   * @param operatorId The id of the operator we wish to match journeys for
   */
  public rematchOperatorJourneys = (operatorId: string) => {
    this.$rootScope.showSpinner = true;
    this.journeyUpdateService.rematchOperatorJourneys({ operatorId: operatorId }).then(() => {
      this.$rootScope.showSpinner = false;
    });
  }

  /**
   * Method to go to the operator edit screen
   *
   * @param operatorId The id of the operator we wish to edit
   */
  public goToOperatorEdit = (operatorId: string) => {
    this.$state.go('operator.edit', { operatorId: operatorId });
  }

  /**
   * Method to go to the operator summary screen
   *
   * @param operator The operator object we wish to view the summary for
   */
  public goToOperatorSummary = (operator: InterACT.Interfaces.TransportOperators.TransportOperator) => {
    this.$state.go('operator.summary', {
      operatorId: operator.id,
      operatorLicence: operator.licenceNumber,
      operatorName: operator.name[this.currentLocale]
    });
  }

  /**
   * Sets a title for display in the primary toolbar and actions to be called from the primary action button
   */
  private activate() {
    this.currentLocale = this.$translate.use();
    this.paginationData = { currentPage: 1, perPageCount: 15 };
    this.selectedScheme = this.userService.selectedScheme;
    this.exportFormats = [
      'PDF',
      'CSV',
      'XLSX'
    ];

    this.breadcrumbService.setBreadcrumbs([
      {
        name: 'BREADCRUMBS.REIMBURSEMENT.OPERATORS.OPERATORS',
        sref: 'operators'
      }
    ]);

    this.toolbarService.getToolbar('primary').then((toolbar: any) => {
      toolbar.setTitle('OPERATORS');
    });
    this.PrimaryAction.setActions([{
      name: 'Create Operator',
      action: this.goToCreate
    }]);
  }

  /**
   * Creates the q param to search for operator records
   * @returns The constructed q param
   */
  private constructQParam = () => {
    const query = this.queryBuilder.newParam().schemesIs(this.selectedScheme.id);

    if (this.$stateParams.status && this.$stateParams.status === 'unlinked') {
      query.and().openSubQuery()
        .not().exists('fields.OID')
        .or().not().exists('fields.SPID')
        .closeSubQuery();
    } else {
      query.and().statusIs(this.statusConstants.ACTIVE);
    }

    if (this.currentFilter) {
      query.and().openSubQuery()
        .fieldContains('licenceNumber', this.currentFilter)
        .or().fieldContains('tradingName', this.currentFilter)
        .or().fieldContains('name.' + this.currentLocale, this.currentFilter)
        .or().fieldContains('address.line1', this.currentFilter)
        .or().fieldContains('fields.OID', this.currentFilter)
        .or().fieldContains('fields.SPID', this.currentFilter)
        .closeSubQuery();
    }

    return query.getParam();
  }

  /**
   * This function is used to set the pagination data for requests
   *
   * @param page the new current page
   * @param perPage the new per page count
   */
  private setPageData = (page?: number, perPage?: number) => {
    if (!_.isUndefined(page)) {
      this.paginationData.currentPage = angular.isNumber(page) ? page : 1;
    }
    this.paginationData.perPageCount = angular.isNumber(perPage) ? perPage : this.paginationData.perPageCount;
  }
}